import React, { useState, useEffect, useRef } from 'react';
import './AdminPanel.css';
import NewsManager from './NewsManager';
import ProductManager from './CropsManage';
import StaffManager from './StaffManager';
import CentersManager from './CentersManager';
import ProcurementManager from './ProcurementManager';
import RtiManager from './RtiManager';
import FarmProductsManager from './FarmProductsManager';
import { getApiUrl } from '../apiConfig';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts';

const ChartStyles = () => (
    <style>{`
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
    `}</style>
);

export default function AdminPanel({ onBack }) {
    const [activeTab, setActiveTab] = useState(() => localStorage.getItem('admin_active_tab') || 'dashboard');
    const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('admin_logged_in') === 'true');
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const submissionLockRef = useRef(false); // Persistent lock that survives re-renders

    // Exit admin and return to previous tab
    const wrapped_onBack = (force = false) => {
        console.log('🚨 wrapped_onBack CALLED, force:', force);

        // Prevent redirect if form is currently submitting - ABSOLUTE SAFEGUARD
        if (!force && (isFormSubmitting || submissionLockRef.current)) {
            console.warn('⛔ REDIRECT BLOCKED - Form is submitting or lock is active!');
            return;
        }

        console.log('✅ Proceeding with onBack()');
        onBack();
    };

    // Persist active tab when it changes
    const handleTabChange = (tab) => {
        console.log('� Admin switching tab to:', tab);

        // Prevent tab change if form is currently submitting
        if (isFormSubmitting) {
            console.log('⛔ Tab change prevented - form is currently submitting!');
            return;
        }

        setActiveTab(tab);
        localStorage.setItem('admin_active_tab', tab);
        setIsSidebarOpen(false);
    };

    // Atomic function to handle both logout and exit at once
    const handleExitAdmin = () => {
        console.log('🚪 Atomic Exit Admin started');

        // 1. Clear admin state
        localStorage.removeItem('admin_logged_in');
        setIsLoggedIn(false);

        // 2. Trigger the exit callback to App.js
        // We pass true to bypass any internal locks
        wrapped_onBack(true);
    };

    // Create a safe submission handler that manages the lock with extended duration
    const handleFormSubmissionState = (isSubmitting) => {
        console.log('📋 handleFormSubmissionState called with:', isSubmitting);

        if (isSubmitting) {
            // Lock activated - set BOTH state and ref
            console.log('🔐 Setting submission lock (BOTH state and ref)');
            submissionLockRef.current = true;
            setIsFormSubmitting(true);
        } else {
            // Lock being released - delay the actual release to extend protection window
            console.log('⏳ Scheduling lock release with extended delay (3.5 seconds)...');
            setTimeout(() => {
                console.log('🔓 Releasing submission lock');
                submissionLockRef.current = false;
                setIsFormSubmitting(false);
            }, 3500);
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        // Real credentials from provided original details
        if (credentials.email === 'admin' && credentials.password === 'admin123') {
            setIsLoggedIn(true);
            localStorage.setItem('admin_logged_in', 'true');
        } else {
            alert('Invalid credentials. Please try again.');
        }
    };

    const handleCPanelClick = () => {
        window.open('https://biz60.lhws.net:2083', '_blank');
    };

    const handleLogout = (force = false) => {
        console.log('🚨 handleLogout CALLED, force:', force);

        // Prevent logout if form is currently submitting (CRITICAL)
        if (!force && (isFormSubmitting || submissionLockRef.current)) {
            console.warn('⛔ Logout BLOCKED - Form is currently submitting or lock is active!');
            return;
        }

        console.log('🔴 Logging out user');
        setIsLoggedIn(false);
        localStorage.removeItem('admin_logged_in');
    };

    // Monitor login state changes
    useEffect(() => {
        if (!isLoggedIn) {
            console.log('ℹ️ Admin is currently in logged-out state (showing login screen)');
        }
    }, [isLoggedIn]);

    // Monitor submission state changes with detailed logging
    useEffect(() => {
        console.log('📊 [ADMIN] isFormSubmitting changed to:', isFormSubmitting);

        if (isFormSubmitting) {
            console.log('🔒 [ADMIN] ===== LOCK ACTIVATED - All navigation BLOCKED =====');
            submissionLockRef.current = true; // Set ref lock
        } else {
            console.log('🔓 [ADMIN] ===== LOCK RELEASED - Navigation ALLOWED =====');
            submissionLockRef.current = false; // Release ref lock
        }
    }, [isFormSubmitting]);

    // Force screen stability for mobile
    useEffect(() => {
        document.body.style.backgroundColor = '#0e633d';
        document.documentElement.style.backgroundColor = '#0e633d';

        try {
            const currentUrl = new URL(window.location.href);
            if (currentUrl.searchParams.get('page') !== 'admin') {
                const newParams = new URLSearchParams(window.location.search);
                newParams.set('page', 'admin');
                const newUrl = window.location.pathname + '?' + newParams.toString();
                window.history.replaceState({}, '', newUrl);
            }
        } catch (e) {
            console.error('URL sync failed:', e);
        }

        return () => {
            document.body.style.backgroundColor = '';
            document.documentElement.style.backgroundColor = '';
        };
    }, []);


    // Detect when AdminPanel is actually unmounting (User is leaving admin panel entirely)
    useEffect(() => {
        console.log('✅ AdminPanel MOUNTED');
        return () => {
            console.log('❌ AdminPanel UNMOUNTING - Component is being removed from DOM');
        };
    }, []); // Empty dependency array ensures this ONLY runs on mount/unmount

    if (!isLoggedIn) {
        return (
            <div className="admin-login-overlay">
                <div className="login-card">
                    <img src="/logo01.png" alt="Logo" className="login-logo" />
                    <div className="login-header">
                        <h2>Admin Portal</h2>
                        <p>Sign in to manage your website</p>
                    </div>
                    <form onSubmit={handleLogin}>
                        <div className="form-group">
                            <label>Email / Username</label>
                            <input
                                type="text"
                                value={credentials.email}
                                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Password</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={credentials.password}
                                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                                    placeholder="Enter password"
                                    required
                                    style={{ paddingRight: '45px' }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(prev => !prev)}
                                    title={showPassword ? "Hide password" : "Show password"}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#64748b',
                                        transition: 'color 0.2s',
                                        outline: 'none'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#0e633d'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                                >
                                    {showPassword ? (
                                        // Eye-off icon (password visible -> click to hide)
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </svg>
                                    ) : (
                                        // Eye icon (password hidden -> click to show)
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                        <button type="submit" className="login-btn">Login</button>
                    </form>
                    <div className="back-link-wrapper">
                        <span className="back-to-site" onClick={handleExitAdmin} style={{ cursor: 'pointer' }}>← Back to Website</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`admin-layout ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <img src="/logo01.png" alt="Logo" />
                    <span>Admin Panel</span>
                    <button className="mobile-close" onClick={() => setIsSidebarOpen(false)}>✕</button>
                </div>
                <nav className="sidebar-nav">
                    <button
                        className={activeTab === 'dashboard' ? 'active' : ''}
                        onClick={() => handleTabChange('dashboard')}
                    >
                        <span className="icon">📊</span> Overview
                    </button>
                    <button
                        className={activeTab === 'views_chart' ? 'active' : ''}
                        onClick={() => handleTabChange('views_chart')}
                    >
                        <span className="icon">📈</span> Views in linechart
                    </button>
                    <button
                        className={activeTab === 'news' ? 'active' : ''}
                        onClick={() => handleTabChange('news')}
                    >
                        <span className="icon">📰</span> Manage News
                    </button>
                    <button
                        className={activeTab === 'crops' ? 'active' : ''}
                        onClick={() => handleTabChange('crops')}
                    >
                        <span className="icon">🌱</span> Crops Production
                    </button>
                    <button
                        className={activeTab === 'staff' ? 'active' : ''}
                        onClick={() => handleTabChange('staff')}
                    >
                        <span className="icon">👥</span> Manage Staff
                    </button>
                    <button
                        className={activeTab === 'centers' ? 'active' : ''}
                        onClick={() => handleTabChange('centers')}
                    >
                        <span className="icon">🏢</span> Manage Centers
                    </button>
                    <button
                        className={activeTab === 'procurement' ? 'active' : ''}
                        onClick={() => handleTabChange('procurement')}
                    >
                        <span className="icon">📑</span> Manage Procurement
                    </button>
                    <button
                        className={activeTab === 'rti' ? 'active' : ''}
                        onClick={() => handleTabChange('rti')}
                    >
                        <span className="icon">⚖️</span> තො.දැ.ගැ. පනත (RTI)
                    </button>
                    <button
                        className={activeTab === 'farm_products' ? 'active' : ''}
                        onClick={() => handleTabChange('farm_products')}
                    >
                        <span className="icon">🌾</span> Manage Farm Products
                    </button>
                    <button
                        className="cpanel-link-btn"
                        onClick={handleCPanelClick}
                        style={{ color: '#ff9800' }}
                    >
                        <span className="icon">⚙️</span> Go to cPanel
                    </button>

                </nav>
                <div className="sidebar-footer">
                    <button className="logout-btn" onClick={handleLogout}>Logout</button>
                </div>
            </aside>

            {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>}

            <main className="admin-content">
                <header className="content-header">
                    <div className="header-left">
                        <button className="menu-toggle" onClick={() => setIsSidebarOpen(true)}>☰</button>
                        <h1>{
                            activeTab === 'views_chart' ? 'Website Views' : 
                            activeTab === 'centers' ? 'Manage Centers (ආයතන හා මධ්‍යස්ථාන)' :
                            activeTab === 'procurement' ? 'Manage Procurement (ප්‍රසම්පාදන)' :
                            activeTab === 'rti' ? 'තොරතුරු දැනගැනීමේ නිලධාරීන් (RTI Officers)' :
                            activeTab === 'farm_products' ? 'ගොවිපළ නිෂ්පාදන (Farm Products)' :
                            activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
                        }</h1>
                    </div>
                    <button className="exit-admin-btn" onClick={handleExitAdmin}>
                        <span className="exit-icon">🚪</span> Exit Admin
                    </button>
                </header>

                <div className="content-body">
                    {activeTab === 'dashboard' && <StatsOverview />}
                    {activeTab === 'views_chart' && <ViewsChartView />}
                    {activeTab === 'news' && <NewsManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'crops' && <ProductManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'staff' && <StaffManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'centers' && <CentersManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'procurement' && <ProcurementManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'rti' && <RtiManager onSubmitting={handleFormSubmissionState} />}
                    {activeTab === 'farm_products' && <FarmProductsManager onSubmitting={handleFormSubmissionState} />}

                </div>
            </main>
        </div>
    );
}

function StatsOverview() {
    const [totalViews, setTotalViews] = useState(0);
    const [newsCount, setNewsCount] = useState(0);

    useEffect(() => {
        const visualUrl = getApiUrl(`api_visuals.php?t=${Date.now()}`);

        const fetchData = () => {
            fetch(visualUrl)
                .then(r => r.json())
                .then(visualData => {
                    const total = Object.values(visualData).reduce((sum, val) => sum + (val || 0), 0);
                    setTotalViews(total);
                })
                .catch(e => console.error(e));

            // Fetch actual news count from server
            const newsUrl = getApiUrl('api_news.php');
            fetch(newsUrl)
                .then(r => r.json())
                .then(newsData => {
                    const count = Array.isArray(newsData) ? newsData.length : 0;
                    setNewsCount(count);
                })
                .catch(e => {
                    console.error("News count fetch fail", e);
                    // Fallback to local storage if API fails
                    const local = JSON.parse(localStorage.getItem('site_news') || '[]');
                    setNewsCount(local.length);
                });
        };

        fetchData();
    }, []);

    return (
        <div className="stats-grid">
            <div className="stat-card">
                <h3>Total Views</h3>
                <p className="stat-value">{totalViews.toLocaleString()}</p>
                <span className="stat-trend up">Based on current tracking</span>
            </div>
            <div className="stat-card">
                <h3>News Published</h3>
                <p className="stat-value">{newsCount}</p>
                <span className="stat-trend">Live from database</span>
            </div>

        </div>
    );
}

function ViewsChartView() {
    const [data, setData] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const loadRealData = (silent = false) => {
        if (!silent) setIsRefreshing(true);
        const url = getApiUrl(`api_visuals.php?t=${Date.now()}`);

        fetch(url)
            .then(r => r.json())
            .then(storedData => {
                const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const chartData = [];
                const safeData = (storedData && typeof storedData === 'object' && !Array.isArray(storedData)) ? storedData : {};

                // Find the date of the most recent Monday
                const currentDate = new Date();
                const currentDay = currentDate.getDay();
                const diffToMonday = currentDay === 0 ? 6 : currentDay - 1;
                const mondayDate = new Date(currentDate);
                mondayDate.setDate(currentDate.getDate() - diffToMonday);

                // Add 7 days starting from Monday
                for (let i = 0; i < 7; i++) {
                    const d = new Date(mondayDate);
                    d.setDate(mondayDate.getDate() + i);

                    // Format correctly using Local Time, NOT UTC (toISOString), to match PHP server
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;

                    const dayName = days[d.getDay()];

                    chartData.push({
                        name: dayName,
                        views: Number(safeData[dateStr]) || 0
                    });
                }
                setData(chartData);
                if (!silent) setTimeout(() => setIsRefreshing(false), 500);
            })
            .catch(e => {
                console.error("Chart fetch error", e);
                setIsRefreshing(false);
            });
    };

    useEffect(() => {
        loadRealData(true);
    }, []);

    return (
        <div className="chart-wrapper" style={{
            padding: '25px',
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #f0f0f0'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div style={{ minWidth: '200px' }}>
                    <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.4rem' }}>Daily Viewers Statistics</h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '1rem' }}>Overall traffic analysis for the current week</p>
                </div>
                <button
                    onClick={() => loadRealData(false)}
                    disabled={isRefreshing}
                    style={{
                        background: isRefreshing ? '#f3f4f6' : '#ecfdf5',
                        color: isRefreshing ? '#9ca3af' : '#059669',
                        padding: '10px 20px',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        border: 'none',
                        cursor: isRefreshing ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                    }}
                >
                    <span style={{
                        display: 'inline-block',
                        animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                    }}>🔄</span>
                    {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                </button>
            </div>
            <ChartStyles />

            <div style={{ width: '100%', height: 'clamp(300px, 60vh, 450px)' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#9ca3af', fontSize: 13 }}
                            dy={10}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#9ca3af', fontSize: 13 }}
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: '12px',
                                border: 'none',
                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                padding: '15px'
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="views"
                            stroke="#10b981"
                            strokeWidth={4}
                            fillOpacity={1}
                            fill="url(#colorViews)"
                            isAnimationActive={false} // Disabled animation
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
